import { useCallback, useEffect, useState } from 'react'
import { ScrollText, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { platformAdminService } from '../../../services/platformAdminService'
import type { AuditLog } from '../../../types/api.types'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { EmptyState } from '../../admin/components/EmptyState'

const ENTITY_OPTIONS = ['', 'user', 'device', 'property', 'organization', 'platform_admin']
const ACTION_OPTIONS = [
  '',
  'platform_create',
  'platform_update',
  'platform_delete',
  'platform_admin_create',
  'platform_admin_update',
  'platform_admin_delete',
]

function ActionBadge({ action }: { action: string }): JSX.Element {
  const isCreate = action.includes('create')
  const isDelete = action.includes('delete')
  const color = isCreate
    ? 'bg-green-100 text-green-700'
    : isDelete
      ? 'bg-red-100 text-red-600'
      : 'bg-blue-100 text-blue-700'
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium ${color}`}>
      {action}
    </span>
  )
}

function PayloadRow({ log }: { log: AuditLog }): JSX.Element {
  const [open, setOpen] = useState(false)
  const hasPayload = log.payload?.after || log.payload?.before

  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50/50">
        <td className="px-4 py-3 text-[13px] text-gray-500">#{log.id}</td>
        <td className="px-4 py-3 text-[13px] font-medium text-gray-700">{log.entity}</td>
        <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
        <td className="px-4 py-3 text-[13px] text-gray-700">{log.actor?.name ?? '—'}</td>
        <td className="px-4 py-3 text-[12px] text-gray-500">{log.actor?.email ?? '—'}</td>
        <td className="px-4 py-3 text-[12px] text-gray-500">
          {new Date(log.created_at).toLocaleString()}
        </td>
        <td className="px-4 py-3 text-center">
          {hasPayload && (
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              className="text-gray-400 hover:text-gray-600"
            >
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
        </td>
      </tr>
      {open && hasPayload && (
        <tr className="bg-gray-50">
          <td colSpan={7} className="px-6 py-3">
            <div className="grid gap-4 text-[12px] sm:grid-cols-2">
              {log.payload.before && (
                <div>
                  <p className="mb-1 font-semibold text-gray-500">Before</p>
                  <pre className="overflow-auto rounded bg-white p-2 text-gray-700 border border-gray-200 max-h-40">
                    {JSON.stringify(log.payload.before, null, 2)}
                  </pre>
                </div>
              )}
              {log.payload.after && (
                <div>
                  <p className="mb-1 font-semibold text-gray-500">After</p>
                  <pre className="overflow-auto rounded bg-white p-2 text-gray-700 border border-gray-200 max-h-40">
                    {JSON.stringify(log.payload.after, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export function AuditLogsTab(): JSX.Element {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [filterEntity, setFilterEntity] = useState('')
  const [filterAction, setFilterAction] = useState('')
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await platformAdminService.getAuditLogs({
        entity: filterEntity || undefined,
        action: filterAction || undefined,
        limit,
        offset,
      })
      setLogs(data)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [filterEntity, filterAction, limit, offset])

  useEffect(() => { loadData() }, [loadData])

  function handleApplyFilters() {
    setOffset(0)
    loadData()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading audit logs...</span>
      </div>
    )
  }

  if (loadError) {
    return <LoadErrorState message="Couldn't load audit logs" onRetry={loadData} />
  }

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[12px] font-medium text-gray-600">Entity</label>
          <select
            value={filterEntity}
            onChange={e => setFilterEntity(e.target.value)}
            className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[13px] text-gray-700 outline-none focus:border-brand-green"
          >
            {ENTITY_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt || 'All Entities'}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-medium text-gray-600">Action</label>
          <select
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[13px] text-gray-700 outline-none focus:border-brand-green"
          >
            {ACTION_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt || 'All Actions'}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleApplyFilters}
          className="h-9 rounded-lg bg-brand-green px-4 text-[13px] font-semibold text-black transition-opacity hover:opacity-70"
        >
          Apply
        </button>

        {(filterEntity || filterAction) && (
          <button
            type="button"
            onClick={() => { setFilterEntity(''); setFilterAction(''); setOffset(0) }}
            className="h-9 rounded-lg border border-gray-200 px-3 text-[13px] text-gray-600 hover:bg-gray-50"
          >
            Clear
          </button>
        )}

        <span className="ml-auto text-[12px] text-gray-500">{logs.length} records</span>
      </div>

      {logs.length === 0 ? (
        <EmptyState icon={ScrollText} message="No audit logs found" hint="Try adjusting your filters" />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="px-4 py-3 text-left text-[12px] font-medium text-gray-500">ID</th>
                  <th className="px-4 py-3 text-left text-[12px] font-medium text-gray-500">Entity</th>
                  <th className="px-4 py-3 text-left text-[12px] font-medium text-gray-500">Action</th>
                  <th className="px-4 py-3 text-left text-[12px] font-medium text-gray-500">Actor</th>
                  <th className="px-4 py-3 text-left text-[12px] font-medium text-gray-500">Actor Email</th>
                  <th className="px-4 py-3 text-left text-[12px] font-medium text-gray-500">Timestamp</th>
                  <th className="px-4 py-3 text-center text-[12px] font-medium text-gray-500">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => <PayloadRow key={log.id} log={log} />)}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              ← Previous
            </button>
            <span className="text-[13px] text-gray-500">
              Showing {offset + 1}–{offset + logs.length}
            </span>
            <button
              type="button"
              disabled={logs.length < limit}
              onClick={() => setOffset(offset + limit)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  )
}
